/**
 * useTextMode — Controls how the app explains things to the user.
 *
 * 2 modes: simple (beginner-friendly wording) | normal (standard wording).
 * Independent from InterfaceMode (what you see vs how it's explained).
 *
 * @since Text Simplification System
 */

import { useCallback } from "react";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { getText, type TextMode } from "@/lib/textRegistry";

// ── Query keys ─────────────────────────────────────

const textModeKeys = {
  current: (userId: string | null) => ["text-mode", userId] as const,
};

// ── Hook ───────────────────────────────────────────

export function useTextMode() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: textMode = "simple" } = useQuery({
    queryKey: textModeKeys.current(user?.id ?? null),
    queryFn: async (): Promise<TextMode> => {
      if (!user) return "simple";
      const { data } = await supabase
        .from("profiles")
        .select("text_mode")
        .eq("user_id", user.id)
        .maybeSingle();
      return ((data as any)?.text_mode as TextMode) ?? "simple";
    },
    enabled: !!user,
    staleTime: 5 * 60_000,
  });

  const { mutate: setTextMode } = useMutation({
    mutationFn: async (newMode: TextMode) => {
      if (!user) return;
      const { error } = await supabase
        .from("profiles")
        .update({ text_mode: newMode } as any)
        .eq("user_id", user.id);
      if (error) throw error;
    },
    onSuccess: (_, newMode) => {
      queryClient.setQueryData(textModeKeys.current(user?.id ?? null), newMode);
      queryClient.invalidateQueries({ queryKey: textModeKeys.current(user?.id ?? null) });
      toast.success(newMode === "simple" ? "Textes simplifiés activés" : "Textes standards activés");
    },
    onError: () => {
      toast.error("Erreur lors du changement de mode texte");
    },
  });

  /** Translate a registry key to the current text mode */
  const t = useCallback(
    (key: string): string => getText(key, textMode),
    [textMode]
  );

  return { textMode, setTextMode, t };
}

export type { TextMode };
